<!--
/**
 * author: vformAdmin
 * email: vdpadmin@163.com
 * website: https://www.vform666.com
 * date: 2021.08.18
 * remark: 如果要分发VForm源码，需在本文件顶部保留此文件头信息！！
 */
-->

<template>
  <el-container class="main-container full-height">
    <el-header class="main-header" v-if="vfLogoHeader">
      <div class="float-left main-title">
        <img src="../../assets/vform-logo.png" @click="openHome" />
        <span class="bold">{{ vfProductName }}</span> {{ vfProductTitle }} <span class="version-span">Ver {{ vFormVersion }}</span>
      </div>
      <div class="float-right external-link">
        <el-dropdown v-if="showLink('languageMenu')" :hide-timeout="2000" @command="handleLanguageChanged">
          <span class="el-dropdown-link">{{ curLangName }}<svg-icon icon-class="el-arrow-down" /></span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="zh-CN">{{ i18nt('application.zh-CN') }}</el-dropdown-item>
              <el-dropdown-item command="en-US">{{ i18nt('application.en-US') }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <a v-if="showLink('externalLink')" href="javascript:void(0)" @click="ev => openUrl(ev, gitUrl)" target="_blank"><svg-icon icon-class="github" />{{ i18nt('application.github') }}</a>
        <a v-if="showLink('externalLink')" href="javascript:void(0)" @click="ev => openUrl(ev, docUrl)" target="_blank"><svg-icon icon-class="document" />{{ i18nt('application.document') }}</a>
        <a v-if="showLink('externalLink')" href="javascript:void(0)" @click="ev => openUrl(ev, chatUrl)" target="_blank">{{ i18nt('application.qqGroup') }}</a>
        <a v-if="showLink('externalLink')" href="javascript:void(0)" @click="ev => openUrl(ev, subScribeUrl)" target="_blank">
          {{ i18nt('application.subscription') }}<i class="el-icon-top-right"></i
        ></a>

        <a href="javascript:void(0)">&nbsp;</a>
      </div>
    </el-header>

    <el-container style="overflow: hidden">
      <el-aside class="side-panel" :style="{ width: leftAsideVisible ? toolBoxWidth : '0px' }">
        <div class="side-panel-wrapper" :style="{ width: toolBoxWidth }">
          <widget-panel :designer="designer" v-if="!toolBoxStyle" />
          <widget-panel-left :designer="designer" v-if="toolBoxStyle" />
        </div>
        <div class="left-aside-toggle-bar" @click="toggleLeftAside">
          <ArrowLeftBold class="icon" style="width: 12px" v-if="leftAsideVisible"></ArrowLeftBold>
          <ArrowRightBold class="icon" style="width: 12px" v-else></ArrowRightBold>
        </div>
      </el-aside>

      <el-container class="center-layout-container">
        <el-header class="toolbar-header">
          <toolbar-panel :designer="designer" :global-dsv="globalDsv" ref="toolbarRef">
            <template v-for="(idx, slotName) in $slots" #[slotName]>
              <slot :name="slotName"></slot>
            </template>
          </toolbar-panel>
        </el-header>
        <el-main class="form-widget-main">
          <div class="container-scroll-bar" style="height: 100%">
            <v-form-widget :designer="designer" :form-config="designer.formConfig" :global-dsv="globalDsv" ref="formRef">
              <template v-for="(idx, slotName) in $slots" #[slotName]="scope">
                <slot :name="slotName" v-bind="scope"></slot>
              </template>
            </v-form-widget>
          </div>
        </el-main>
      </el-container>

      <el-aside class="setting-panel" :style="{ width: rightAsideVisible ? '300px' : '0px' }">
        <div class="side-panel-wrapper">
          <setting-panel :designer="designer" :selected-widget="designer.selectedWidget" :form-config="designer.formConfig" :global-dsv="globalDsv" />
        </div>
        <div class="right-aside-toggle-bar" @click="toggleRightAside">
          <ArrowRightBold style="width: 12px" v-if="rightAsideVisible" />
          <ArrowLeftBold style="width: 12px" v-else />
        </div>
      </el-aside>
    </el-container>
  </el-container>
</template>

<script>
import { computed, getCurrentInstance, reactive, ref, nextTick, toRefs, provide, onMounted } from 'vue';

import WidgetPanel from './widget-panel/index';
import WidgetPanelLeft from './widget-panel/index-left';
import ToolbarPanel from './toolbar-panel/index';
import SettingPanel from './setting-panel/index';
import VFormWidget from './form-widget/index';
import { addWindowResizeHandler, deepClone, getQueryParam, getAllContainerWidgets, getAllFieldWidgets, traverseAllWidgets } from '@/utils/util';
import { MOCK_CASE_URL, VARIANT_FORM_VERSION } from '@/utils/config';
import { useI18n, useChangeLocale } from '@/utils/i18n';

import { createDesigner } from '@/components/form-designer/designer';
import SvgIcon from '@/components/svg-icon/index';
import { ArrowLeftBold, ArrowRightBold } from '@element-plus/icons-vue';
export default {
  components: {
    ArrowLeftBold,
    ArrowRightBold,
    SettingPanel,
    SvgIcon,
    ToolbarPanel,
    VFormWidget,
    WidgetPanel,
    WidgetPanelLeft,
  },
  name: 'VmFormDesigner',
  props: {
    /* 禁止显示的组件名称数组 */
    bannedWidgets: {
      default: () => [],
      type: Array,
    },

    /* 设计器配置参数 */
    designerConfig: {
      default: () => {
        return {
          clearDesignerButton: true, // 是否显示清空设计器按钮
          eventCollapse: true, // 是否显示组件事件属性折叠面板
          exportCodeButton: true, // 是否显示导出代码按钮
          exportJsonButton: true, // 是否显示导出JSON器按钮
          externalLink: true, // 是否显示GitHub、文档等外部链接

          formTemplates: true, // 是否显示表单模板
          importJsonButton: true, // 是否显示导入JSON按钮
          languageMenu: true, // 是否显示语言切换菜单
          languageName: 'zh-CN', // 界面语言，默认显示中文
          logoHeader: true, // 是否显示Logo头部区域（仅Pro）
          presetCssCode: '', // 设计器预设CSS样式代码

          previewFormButton: true, // 是否显示预览表单按钮
          productName: '', // 自定义表单设计器名称，对应“VForm Pro”（仅Pro）

          productTitle: '', // 自定义表单设计器标题，对应“表单设计器”（仅Pro）
          resetFormJson: false, // 是否在设计器初始化时将表单内容重置为空

          toolBoxStyle: 'left', //组件面板位置，可选值：left、top
          toolbarMaxWidth: 420, // 设计器工具按钮栏最大宽度（单位像素）

          toolbarMinWidth: 300, // 设计器工具按钮栏最小宽度（单位像素）
          widgetNameReadonly: false, // 禁止修改组件名称
        };
      },
      type: Object,
    },
    /* 后端字段列表API */
    fieldListApi: {
      default: null,
      type: Object,
    },
    /* 全局数据源变量 */
    globalDsv: {
      default: () => ({}),
      type: Object,
    },
  },
  setup(props, context) {
    const { i18nt } = useI18n();
    const { proxy } = getCurrentInstance();
    let $current = proxy;

    /* 页面组件 */
    const toolbarRef = ref(null);
    const formRef = ref(null);

    /* 声明周期事件定义 */
    onMounted(() => {
      initLocale();

      loadCase();
      loadFieldListFromServer();
    });

    const data = reactive({
      chatUrl: 'https://vform666.com/pages/chat-group/',
      curLangName: ref(''),
      curLocale: '',

      designer: createDesigner(proxy),
      docUrl: 'https://www.yuque.com/vform-mobile/doc/',
      externalComponents: {}, // 外部组件实例集合,
      fieldList: [],

      gitUrl: 'https://gitee.com/vdpadmin/vform-mobile-builds',

      leftAsideVisible: true,

      rightAsideVisible: true,

      scrollerHeight: 0,
      subScribeUrl: 'https://vform666.com/pages/mobile-pro/',

      vFormVersion: VARIANT_FORM_VERSION,
      vformInfo: proxy.$vformInfo,
    });

    provide('serverFieldList', data.fieldList);
    provide('getBannedWidgets', () => {
      return props.bannedWidgets;
    });
    provide('getDesignerConfig', () => {
      return props.designerConfig;
    });

    /* 初始化设计器参数 */
    data.vsCodeFlag = getQueryParam('vscode') === 1;
    data.caseName = getQueryParam('case');

    const vfProductName = computed(() => {
      if (import.meta.env.VITE_APP_ENV_STANDARD) {
        return 'VForm Mobile';
      }

      return (props.designerConfig && props.designerConfig.productName) || (data.vformInfo && data.vformInfo.productName) || 'VForm Mobile';
    });
    const vfProductTitle = computed(() => {
      if (import.meta.env.VITE_APP_ENV_STANDARD) {
        return i18nt('application.productTitle');
      }
      return (props.designerConfig && props.designerConfig.productTitle) || (data.vformInfo && data.vformInfo.productTitle) || i18nt('application.productTitle');
    });

    const vfLogoHeader = computed(() => {
      if (import.meta.env.VITE_APP_ENV_STANDARD) {
        return true;
      }

      if (props.designerConfig.logoHeader === undefined) return true;
      else return props.designerConfig.logoHeader;
    });

    const toolBoxStyle = computed(() => {
      return props.designerConfig.toolBoxStyle === 'left';
    });

    const toolBoxWidth = computed(() => {
      return toolBoxStyle.value ? '312px' : '260px';
    });

    const testEEH = (eventName, eventParams) => {};

    const showLink = configName => {
      if (props.designerConfig[configName] === undefined) {
        return true;
      }

      return !!props.designerConfig[configName];
    };

    /* 国际化相关 */
    const initLocale = () => {
      data.curLocale = localStorage.getItem('vm_form_locale');
      if (data.vsCodeFlag) {
        data.curLocale = data.curLocale || 'en-US';
      } else {
        data.curLocale = data.curLocale || 'zh-CN';
      }

      data.curLangName = i18nt('application.' + data.curLocale);
      changeLanguage(data.curLocale);
    };
    const changeLanguage = langName => {
      useChangeLocale(langName);
    };
    const handleLanguageChanged = command => {
      changeLanguage(command);
      data.curLangName = i18nt('application.' + command);
    };

    /* 加载示例 */
    const loadCase = () => {
      if (!data.caseName) {
        return;
      }

      axios
        .get(MOCK_CASE_URL + data.caseName + '.txt')
        .then(res => {
          if (res.data.code) {
            $current.$message.error(i18nt('designer.hint.sampleLoadedFail'));
            return;
          }

          setFormJson(res.data);
          $current.$message.success(i18nt('designer.hint.sampleLoadedSuccess'));
        })
        .catch(error => {
          $current.$message.error(i18nt('designer.hint.sampleLoadedFail') + ':' + error);
        });
    };
    const loadFieldListFromServer = () => {
      if (!props.fieldListApi) {
        return;
      }

      let headers = props.fieldListApi.headers || {};
      axios
        .get(props.fieldListApi.URL, { headers })
        .then(res => {
          let labelKey = props.fieldListApi.labelKey || 'label';
          let nameKey = props.fieldListApi.nameKey || 'name';

          data.fieldList.splice(0); // 清空已有
          res.data.forEach(fieldItem => {
            data.fieldList.push({
              label: fieldItem[labelKey],
              name: fieldItem[nameKey],
            });
          });
        })
        .catch(error => {
          $current.$message.error(error);
        });
    };

    /* 其他方法 */
    const openHome = () => {
      if (data.vsCodeFlag) {
        const msgObj = {
          cmd: 'openUrl',
          data: {
            url: 'https://www.vform666.com/',
          },
        };
        window.parent.postMessage(msgObj, '*');
      }
    };
    const openUrl = (event, url) => {
      if (data.vsCodeFlag) {
        const msgObj = {
          cmd: 'openUrl',
          data: {
            url,
          },
        };
        window.parent.postMessage(msgObj, '*');
      } else {
        let aDom = event.currentTarget;
        aDom.href = url;
        // window.open(url, '_blank') //直接打开新窗口，会被浏览器拦截
      }
    };

    /* 对外暴露方法 */

    const setFormJson = formJson => {
      let modifiedFlag = false;
      if (formJson) {
        if (typeof formJson === 'string') {
          modifiedFlag = data.designer.loadFormJson(JSON.parse(formJson));
        } else if (formJson.constructor === Object) {
          modifiedFlag = data.designer.loadFormJson(formJson);
        }

        if (modifiedFlag) {
          data.designer.emitHistoryChange();
        }
      }
    };

    const getFormJson = () => {
      return {
        formConfig: deepClone(data.designer.formConfig),
        widgetList: deepClone(data.designer.widgetList),
      };
    };

    const clearDesigner = () => {
      toolbarRef.value.clearFormWidget();
    };

    /**
     * 刷新表单设计器
     */
    const refreshDesigner = () => {
      // this.designer.loadFormJson( this.getFormJson() )  //只有第一次调用生效？？
      let fJson = getFormJson();
      data.designer.clearDesigner(true); // 不触发历史记录变更
      data.designer.loadFormJson(fJson);
    };

    /**
     * 预览表单
     */
    const previewForm = () => {
      toolbarRef.value.previewForm();
    };

    /**
     * 导入表单JSON
     */
    const importJson = () => {
      toolbarRef.value.importJson();
    };

    /**
     * 导出表单JSON
     */
    const exportJson = () => {
      toolbarRef.value.exportJson();
    };

    /**
     * 导出Vue/HTML代码
     */
    const exportCode = () => {
      toolbarRef.value.exportCode();
    };

    /**
     * 获取所有字段组件
     * @param widgetList 默认为null，代表取当前表单json组件列表
     * @param staticWidgetsIncluded 是否包含按钮等静态组件，默认不包含
     * @returns {*[]}
     */
    const getFieldWidgets = (widgetList = null, staticWidgetsIncluded = false) => {
      return widgetList ? getAllFieldWidgets(widgetList, staticWidgetsIncluded) : getAllFieldWidgets(data.designer.widgetList, staticWidgetsIncluded);
    };

    /**
     * 获取所有容器组件
     * @param widgetList 默认为null，代表取当前表单json组件列表
     * @returns {*[]}
     */
    const getContainerWidgets = (widgetList = null) => {
      return widgetList ? getAllContainerWidgets(widgetList) : getAllContainerWidgets(data.designer.widgetList);
    };

    /**
     * 升级表单json，以补充最新的组件属性
     * @param formJson
     */
    const upgradeFormJson = formJson => {
      if (!formJson.widgetList || !formJson.formConfig) {
        data.$message.error('Invalid form json!');
        return;
      }

      traverseAllWidgets(formJson.widgetList, w => {
        data.designer.upgradeWidgetConfig(w);
      });
      data.designer.upgradeFormConfig(formJson.formConfig);

      return formJson;
    };

    const getWidgetRef = (widgetName, showError = false) => {
      return formRef.value.getWidgetRef(widgetName, showError);
    };

    const getSelectedWidgetRef = () => {
      return formRef.value.getSelectedWidgetRef();
    };

    /**
     * 添加数据源对象
     * @param dsObj
     */
    const addDataSource = dsObj => {
      data.designer.formConfig.dataSources.push(dsObj);
    };

    /**
     * 增加外部组件引用，可通过getEC()方法获取外部组件，以便在VForm内部调用外部组件方法
     * @param componentName 外部组件名称
     * @param externalComponent 外部组件实例
     */
    const addEC = (componentName, externalComponent) => {
      data.externalComponents[componentName] = externalComponent;
    };

    /**
     * 判断外部组件是否可获取
     * @param componentName 外部组件名称
     * @returns {boolean}
     */
    const hasEC = componentName => {
      return data.externalComponents.hasOwnProperty(componentName);
    };

    /**
     * 获取外部组件实例
     * @param componentName
     * @returns {*}
     */
    const getEC = componentName => {
      return data.externalComponents[componentName];
    };

    const toggleLeftAside = () => {
      data.leftAsideVisible = !data.leftAsideVisible;
    };

    const toggleRightAside = () => {
      data.rightAsideVisible = !data.rightAsideVisible;
    };

    // 初始化设计器
    data.designer.initDesigner(!!data.designer.resetFormJson);
    data.designer.loadFormContentFromStorage();

    return {
      formRef,
      /* 页面组件引用 */
      toolbarRef,

      /* 属性 */
      ...toRefs(data),
      ...props,
      addDataSource,
      addEC,

      /* 暴露方法 */
      changeLanguage,
      clearDesigner,
      exportCode,
      exportJson,
      getContainerWidgets,
      getEC,
      getFieldWidgets,
      getFormJson,

      getSelectedWidgetRef,
      getWidgetRef,
      handleLanguageChanged,
      hasEC,

      /* 方法 */
      i18nt,
      importJson,
      openHome,
      openUrl,
      previewForm,
      refreshDesigner,
      setFormJson,
      showLink,
      testEEH,
      toggleLeftAside,
      toggleRightAside,
      toolBoxStyle,
      toolBoxWidth,
      upgradeFormJson,
      vfLogoHeader,

      /* 计算属性 */
      vfProductName,
      vfProductTitle,
    };
  },
};
</script>
<style>
:root:root {
  --van-number-keyboard-z-index: 3000;
}
</style>
<style lang="scss" scoped>
.el-container.main-container {
  &.full-height {
    display: flex;
  }

  .el-container.main-content {
    display: inline-flex;
    height: 100%;
    width: 100%;
    flex-direction: row;
    flex-shrink: 1;
    overflow: hidden;

    .el-aside.side-panel {
      height: 100%;
    }

    .el-main.form-widget-main {
      :deep(.el-scrollbar) {
        .el-scrollbar__view {
          height: 100%;
        }
      }
    }

    .el-aside.setting-pannel {
      height: 100%;
      overflow-y: hidden;
      display: flex;
      flex-basis: auto;

      :deep(.panel-container) {
        padding: 0px;

        .el-tabs {
          display: flex;
          flex-direction: column;
        }
        .el-tabs__header {
          padding: 0 8px;
        }
        .el-tabs__content,
        .el-tab-pane {
          height: 100%;
          flex-grow: 1;
        }
        .el-scrollbar__wrap {
          padding-left: 8px;
          padding-right: 12px;
        }
      }
    }
  }

  .form-widget-container {
    overflow-y: hidden;
    height: 100%;
    background: #f8f8f8;
    box-sizing: border-box;

    :deep(.van-form.full-height-width) {
      box-sizing: border-box;
      overflow-y: auto;

      .form-widget-canvas {
        height: 100%;
        box-sizing: border-box;
      }
    }
  }
}

.el-container.main-container {
  background: #fff;

  :deep(aside) {
    /* 防止aside样式被外部样式覆盖！！ */
    margin: 0;
    padding: 0;
    background: inherit;
  }
}

.el-container.full-height {
  height: 100%;
  overflow-y: hidden;
}

.el-container.center-layout-container {
  min-width: 680px;
  border-left: 2px dotted #ebeef5;
  border-right: 2px dotted #ebeef5;
}

.el-header.main-header {
  border-bottom: 2px dotted #ebeef5;
  height: 48px !important;
  line-height: 48px !important;
  min-width: 800px;
  //background: #f5f7fa;
}

div.main-title {
  font-size: 18px;
  color: #242424;
  display: flex;
  align-items: center;
  justify-items: center;

  img {
    cursor: pointer;
    width: 36px;
    height: 36px;
  }

  span.bold {
    font-size: 20px;
    font-weight: bold;
    margin: 0 6px 0 6px;
  }

  span.version-span {
    font-size: 14px;
    color: #101f1c;
    margin-left: 6px;
  }
}

.float-left {
  float: left;
}

.float-right {
  float: right;
}

.el-dropdown-link {
  margin-right: 12px;
  cursor: pointer;
}

div.external-link {
  display: flex;
  align-items: center;
  justify-content: center;

  a {
    font-size: 13px;
    text-decoration: none;
    margin-right: 10px;
    color: #606266;
  }
}

.el-header.toolbar-header {
  font-size: 14px;
  border-bottom: 1px dotted #cccccc;
  height: 42px !important;
  //line-height: 42px !important;
}

.el-aside.side-panel {
  position: relative;
  overflow: initial;
  transition: 0.5s;
  .side-panel-wrapper {
    width: 260px;
    overflow: hidden;
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
  }
}

.el-main.form-widget-main {
  padding: 0;

  position: relative;
  overflow-x: hidden;
}

.el-aside.setting-panel {
  height: 100%;
  position: relative;
  overflow: initial;
  transition: 0.5s;

  :deep(.panel-container) {
    padding: 0;

    .el-tabs {
      display: flex;
      flex-direction: column;
    }
    .el-tabs__header {
      padding: 0 8px;
    }
    .el-tabs__content,
    .el-tab-pane {
      height: 100%;
      flex-grow: 1;
    }
    .el-scrollbar__wrap {
      padding-left: 8px;
      padding-right: 12px;
    }
  }
  .side-panel-wrapper {
    width: 300px;
    overflow: hidden;
    position: absolute;

    display: flex;
    flex-basis: auto;

    top: 0px;
    left: 0px;
    bottom: 0px;
  }
}

.left-aside-toggle-bar {
  display: block;
  cursor: pointer;
  height: 36px;
  width: 12px;
  position: absolute;
  top: calc(50% - 26px);
  right: -12px;
  border-radius: 0 8px 8px 0;
  background: #fff;
  z-index: 8;
  padding-top: 16px;
  box-shadow: 3px 0px 3px #dddddd;

  .icon {
    font-size: 18px;
    color: #909399;
    height: 20px;
    width: 20px;
    margin-left: 0px;
  }

  &-wide {
    left: 320px;
  }
}

.left-aside-toggle-bar:hover {
  .icon {
    color: $--color-primary;
  }
}

.right-aside-toggle-bar {
  display: block;
  cursor: pointer;
  height: 36px;
  width: 12px;
  position: absolute;
  top: calc(50% - 26px);
  left: -12px;
  border-radius: 8px 0 0 8px;
  background: #fff;
  z-index: 8;
  padding-top: 16px;
  box-shadow: -3px 0px 3px #dddddd;

  .icon {
    font-size: 18px;
    color: #909399;
    height: 20px;
    width: 20px;
    margin-left: 0px;
  }
}

.right-aside-toggle-bar:hover {
  .icon {
    color: $--color-primary;
  }
}

.container-scroll-bar {
  :deep(.el-scrollbar__wrap),
  :deep(.el-scrollbar__view) {
    overflow-x: hidden;
  }
}
</style>
